import gql from 'graphql-tag';
import * as fragments from './policy.query.fragments';

export const CONFIRM = gql`
  mutation confirmPolicyChange(
    $policyId: ID!
    $previewId: ID!
    $internalNotes: String!
    $uwReviewAnswers: AWSJSON
    $suppressCommunications: Boolean
  ) {
    confirmPolicyChange(
      policyId: $policyId
      previewId: $previewId
      internalNotes: $internalNotes
      uwReviewAnswers: $uwReviewAnswers
      suppressCommunications: $suppressCommunications
    ) {
      success
      reason
    }
  }
`;

export const SUBMIT_CHANGES_FOR_APPROVAL = gql`
  mutation sendPolicyChangesForApprovalCommunication(
    $policyId: ID!
    $policyPreviewLink: String!
    $policyChangesDescription: String!
    $customerName: String!
    $changesSummary: String
  ) {
    sendPolicyChangesForApprovalCommunication(args: {
      policyId: $policyId
      policyPreviewLink: $policyPreviewLink
      policyChangesDescription: $policyChangesDescription
      customerName: $customerName
      changesSummary: $changesSummary
    }) {
      success
    }
  }
`;

export const SUBMIT_FARM_POLICY_FOR_UNDERWRITING_APPROVAL = gql`
  mutation submitUWReviewForApproval(
    $previewId: ID!
    $policyId: ID!
    $policyVersion: Int!
    $answers: AWSJSON!
    $internalNote: String
  ) {
    submitUWReviewForApproval(
      previewId: $previewId
      policyId: $policyId
      policyVersion: $policyVersion
      answers: $answers
      internalNote: $internalNote
    ) {
      success
      uwReviewDraftId
    }
  }
`;

export const REQUEST_TO_RESUBMIT_POLICY_CHANGES_FOR_APPROVAL = gql`
  mutation sendResubmitChangesForApprovalCommunication($policyLink: String!, $agentUsername: String!) {
    sendResubmitChangesForApprovalCommunication(args: { policyLink: $policyLink, agentUsername: $agentUsername }) {
      success
      agentEmail
    }
  }
`;

export const CHANGE_POLICY = gql`
  mutation previewPolicyChangePolicy($policy: PolicyInput!, $fromPreviewId: ID) {
    previewPolicyChangePolicy(policy: $policy, fromPreviewId: $fromPreviewId) {
      ...PolicyPreviewResponse
    }
  }
  ${fragments.PolicyPreviewResponse}
`;

// Dedicated cancellation / reinstatement preview mutations (issue #31365). The shared
// PolicyPreviewResponse fragment keeps the preview shape identical to previewPolicyChangePolicy.
export const CANCEL_POLICY = gql`
  mutation previewPolicyCancellation($policy: PolicyCancellationInput!, $fromPreviewId: ID) {
    previewPolicyCancellation(policy: $policy, fromPreviewId: $fromPreviewId) {
      ...PolicyPreviewResponse
    }
  }
  ${fragments.PolicyPreviewResponse}
`;

export const REINSTATE_POLICY = gql`
  mutation previewPolicyReinstatement($policy: PolicyReinstatementInput!, $fromPreviewId: ID) {
    previewPolicyReinstatement(policy: $policy, fromPreviewId: $fromPreviewId) {
      ...PolicyPreviewResponse
    }
  }
  ${fragments.PolicyPreviewResponse}
`;

export const ADD_SEGMENT = gql`
  mutation previewPolicyChangeAddSegment($policyId: ID!, $segment: PolicyDetailsInput!, $pendingAdditionalParties: [AdditionalPartyDetailsInput]) {
    previewPolicyChangeAddSegment(policyId: $policyId, segment: $segment, pendingAdditionalParties: $pendingAdditionalParties) {
      ...PolicyPreviewResponse
    }
  }
  ${fragments.PolicyPreviewResponse}
`;
export const CHANGE_SEGMENT = gql`
  mutation previewPolicyChangeToSegment($policyId: ID!, $segmentId: ID!, $segment: PolicyDetailsInput!, $clarionDoorIdOverride: String, $fromPreviewId: ID) {
    previewPolicyChangeToSegment(policyId: $policyId, segmentId: $segmentId, segment: $segment, clarionDoorIdOverride: $clarionDoorIdOverride, fromPreviewId: $fromPreviewId) {
      ...PolicyPreviewResponse
    }
  }
  ${fragments.PolicyPreviewResponse}
`;

export const ADD_HOLD_CARD = gql`
  mutation addHoldCard($policyId: ID!, $reason: String!) {
    addHoldCard(policyId: $policyId, reason: $reason) {
      policyId
      createdBy
      createdDate
      reason
      deleted
      success
      holdCardId
    }
  }
`;
export const PREVIEW_POLICY_CHANGE_SCRUB_INCIDENTS = gql`
  mutation previewPolicyChangeScrubIncidents(
    $policy: ScrubIncidentsInput!
    $incidentDates: [AWSDate]!
    $claimsToScrub: [ClaimToScrubInput]
  ) {
    previewPolicyChangeScrubIncidents(policyData: $policy, incidentDates: $incidentDates, claimsToScrub: $claimsToScrub) {
      ...PolicyPreviewResponse
    }
  }
  ${fragments.PolicyPreviewResponse}
`;
export const PREVIEW_POLICY_CHANGE_ADD_INCIDENTS = gql`
  mutation previewPolicyChangeAddIncidents(
    $policy: AddIncidentsInput!
    $autoViolationsToAdd: [AutoViolationToAddInput!]
    $homeClaimsToAdd: [HomeClaimToAddInput!]
    $homeViolationsToAdd: [HomeViolationToAddInput!]
  ) {
    previewPolicyChangeAddIncidents(
      policyData: $policy
      autoViolationsToAdd: $autoViolationsToAdd
      homeClaimsToAdd: $homeClaimsToAdd
      homeViolationsToAdd: $homeViolationsToAdd
    ) {
      ...PolicyPreviewResponse
    }
  }
  ${fragments.PolicyPreviewResponse}
`;
export const GET_HOLD_CARDS = gql`
  query ($policyId: ID!) {
    holdcards: getHoldCards(policyId: $policyId) {
      holdCardId
      createdBy
      createdDate
      reason
      deleted
      policyId
    }
  }
`;

export const REMOVE_HOLD_CARD = gql`
  mutation removeHoldCard($policyId: ID!, $holdCardId: ID!) {
    removeHoldCard(policyId: $policyId, holdCardId: $holdCardId) {
      holdCardId
      deleted
      policyId
    }
  }
`;

export const RECREATE_APPLICATION = gql`
  mutation recreatePolicyApplication($policyId: ID!) {
    pdfUrl: recreatePolicyApplication(policyId: $policyId)
  }
`;

export const GET_POLICY_PAID_AND_PENDING = gql`
  query ($policyId: ID!) {
    PolicyPaidAndPendingCash: getPolicyPaidAndPendingCash(policyId: $policyId) {
      currentTotalPaid
      pendingAmount
    }
  }
`;

export const GET_RENEWAL_POLICY_ID = gql`
  query ($policyId: ID!) {
    renewalPolicyId: getRenewalPolicyId(policyId: $policyId)
  }
`;

// An earlier term's rated home claims and its bound offer's, for the Incidents tab's removal history (#36608)
export const GET_HOME_CLAIM_HISTORY = gql`
  query ($id: ID!, $accountId: ID!) {
    policy: getPolicy(id: $id, accountId: $accountId) {
      id
      term
      effectiveDate
      segments {
        homeClaimDates
      }
      offer {
        id
        quote {
          homeClaimDates
        }
      }
    }
  }
`;

export const GET_RENEWAL_PRICE_COMPARISON = gql`
  query ($currentPolicyId: ID!, $previousPolicyId: ID!) {
    RenewalPriceComparison: getRenewalPriceComparison(
      currentPolicyId: $currentPolicyId
      previousPolicyId: $previousPolicyId
    ) {
      priorTerm {
        premium
        surplus
        fees
        totalPrice
      }
      normalizedPriorTerm {
        premium
        surplus
        fees
        totalPrice
      }
      currentTerm {
        premium
        surplus
        fees
        totalPrice
      }
      discountChanges {
        key
        label
        status
      }
      relevantFactors {
        key
        label
        detail
      }
    }
  }
`;

export const ORDER_INSPECTION = gql`
  mutation orderInspection(
    $policyId: ID!
    $address: AddressDetailsInput!
    $mailingAddress: AddressDetailsInput!
    $fname: String!
    $lname: String!
    $phone: String!
    $coverageA: Float!
    $effectiveDate: String!
  ) {
    inspection: orderInspection(
      policyId: $policyId
      address: $address
      mailingAddress: $mailingAddress
      fname: $fname
      lname: $lname
      phone: $phone
      coverageA: $coverageA
      effectiveDate: $effectiveDate
    ) {
      ... on OrderInspectionSuccess {
        dateOrdered
        status
        policyId
        caseNumber
        inspectionId
      }
      ... on OrderInspectionError {
        error
      }
    }
  }
`;

export const GET_INSPECTION_STATUS = gql`
  query ($policyId: ID!) {
    inspection: getInspectionStatus(policyId: $policyId) {
      status
      caseNumber
      inspectionId
      dateOrdered
      dateCompleted
      completedType
    }
  }
`;

export const GET_CD_DIFF = gql`
  query (
    $policyId: String
    $policyPreviewId: String
    $cdIdBefore: String
    $cdIdAfter: String
    $invokedBy: String
  ) {
    requests: getPolicyPreviewCdRequestData(
      policyId: $policyId
      policyPreviewId: $policyPreviewId
      cdIdBefore: $cdIdBefore
      cdIdAfter: $cdIdAfter
      invokedBy: $invokedBy
    ) {
      diff
      requestIdAfterPreview
      requestIdBeforePreview
      xmlBeforePreview {
        success
        message
        data
      }
      xmlAfterPreview {
        success
        message
        data
      }
    }
  }
`;

export const GET_BIX_CONVERSION_SIGNATURE_FROM_TABLE = gql`
  query getBixConversationSignatureFromTable($policyId: ID!) {
    getBixConversationSignatureFromTable(policyId: $policyId) {
      policyId
      signedTimestampISO8601
    }
  }
`;

export const GET_PALOMAR_POLICY_DOC = gql`
  query getPalomarPolicyDoc($palomarPolicyId: String!, $repEmail: String!, $policyType: String!, $policyId: ID) {
    palomarPolicyDoc: getPalomarPolicyDoc(
      palomarPolicyId: $palomarPolicyId
      repEmail: $repEmail
      policyType: $policyType
      policyId: $policyId
    ) {
      palomarPolicyDecPage
    }
  }
`;

export const REWRITE_POLICY = gql`
  mutation rewritePolicy($policyId: ID!, $newEffectiveDate: AWSDate!, $internalNotes: String, $newPolicyId: String) {
    rewritePolicy(
      policyId: $policyId
      newEffectiveDate: $newEffectiveDate
      internalNotes: $internalNotes
      newPolicyId: $newPolicyId
    ) {
      success
      message
    }
  }
`;

export const GENERATE_MEMBER_FORM = gql`
    mutation generateMemberForm($documentType: String!, $memberEmail: String, $policyId: ID!, $previewId: ID!) {
    generateMemberForm(args: {
      documentType: $documentType
      memberEmail: $memberEmail
      policyId: $policyId
      previewId: $previewId
    }) {
      documentLink
    }
  }
`;

export const GET_POLICY_AUTO_PREMIUM_BREAKDOWN_PDF = gql`
  query getPolicyAutoPremiumBreakdownPdf($policyId: ID!, $accountId: ID!, $segmentId: ID) {
    getPolicyAutoPremiumBreakdownPdf(policyId: $policyId, accountId: $accountId, segmentId: $segmentId)
  }
`;
